/**
 * Login screen. Two preconfigured accounts only — no register, no recover.
 * Renders a form, posts to /login on submit, and reports back via callback.
 */

export interface LoginCallbacks {
  /** Called with the credentials. Returns null on success, or an error
   *  message string to display below the form on failure. */
  onSubmit: (username: string, password: string) => Promise<string | null>;
}

export class LoginUI {
  private root: HTMLElement;
  private cb: LoginCallbacks;
  private title: string;
  private form!: HTMLFormElement;
  private errEl!: HTMLElement;
  private submitBtn!: HTMLButtonElement;

  constructor(root: HTMLElement, cb: LoginCallbacks, title = 'Princhess') {
    this.root = root;
    this.cb = cb;
    this.title = title.replace(/[&<>"']/g, ''); // plain text only
    this.render();
  }

  private render(): void {
    this.root.innerHTML = `
      <div class="lobby">
        <h1 class="title">${this.title}</h1>
        <form class="challenge-card login-card" data-form>
          <h2>Sign in</h2>
          <label class="field">
            <span>Username</span>
            <input name="username" autocomplete="username" autocapitalize="off" autocorrect="off" spellcheck="false" required />
          </label>
          <label class="field">
            <span>Password</span>
            <input name="password" type="password" autocomplete="current-password" required />
          </label>
          <p class="login-error" data-error hidden></p>
          <button type="submit" class="primary" data-submit>Sign in</button>
        </form>
      </div>
    `;
    this.form = this.root.querySelector('[data-form]') as HTMLFormElement;
    this.errEl = this.root.querySelector('[data-error]') as HTMLElement;
    this.submitBtn = this.root.querySelector('[data-submit]') as HTMLButtonElement;
    this.form.addEventListener('submit', (e) => void this.handleSubmit(e));
    setTimeout(() => (this.form.querySelector('[name=username]') as HTMLInputElement).focus(), 0);
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const data = new FormData(this.form);
    const username = String(data.get('username') ?? '').trim();
    const password = String(data.get('password') ?? '');
    if (!username || !password) return;
    this.setError(null);
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Signing in…';
    try {
      const err = await this.cb.onSubmit(username, password);
      if (err) {
        this.setError(err);
      }
    } finally {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = 'Sign in';
    }
  }

  private setError(msg: string | null): void {
    if (msg) {
      this.errEl.textContent = msg;
      this.errEl.hidden = false;
    } else {
      this.errEl.textContent = '';
      this.errEl.hidden = true;
    }
  }
}
